import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Download, FileCheck, AlertCircle } from "lucide-react";
import { generateNILGoExport, validateDealsForNILGo } from "@/services/nilGoExportService";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function NILGoExport() {
  const [selectedDeals, setSelectedDeals] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const { data: approvedDeals, isLoading } = useQuery({
    queryKey: ["approved-deals-for-export"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select(`
          *,
          athletes (name, sport)
        `)
        .eq("status", "active")
        .order("approved_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: submissionHistory } = useQuery({
    queryKey: ["nil-go-submissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nil_go_submissions")
        .select("*")
        .order("submitted_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    },
  });

  const toggleDeal = (dealId: string) => {
    setSelectedDeals(prev =>
      prev.includes(dealId)
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const selectAll = () => {
    if (selectedDeals.length === approvedDeals?.length) {
      setSelectedDeals([]);
    } else {
      setSelectedDeals(approvedDeals?.map(d => d.id) || []);
    }
  };

  const handleExport = async () => {
    if (selectedDeals.length === 0) {
      toast.error("Please select at least one deal to export");
      return;
    }

    setIsExporting(true);
    try {
      // Validate deals first
      const validation = await validateDealsForNILGo(selectedDeals);
      
      if (!validation.valid) {
        toast.error("Validation Failed", {
          description: validation.errors.join("\n"),
        });
        setIsExporting(false);
        return;
      }

      // Generate export
      const fileName = await generateNILGoExport(selectedDeals);
      
      toast.success("NIL Go Export Generated", {
        description: `${selectedDeals.length} deals exported to ${fileName}`,
      });

      // Clear selection
      setSelectedDeals([]);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">NIL Go Export</h1>
          <p className="text-muted-foreground">
            Generate CSC-compliant NIL Go submission files
          </p>
        </div>

        <Alert>
          <FileCheck className="h-4 w-4" />
          <AlertDescription>
            Only approved deals with complete VBP and FMV justification can be exported to NIL Go.
            Ensure all required fields are populated before submission.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle>Select Deals for Export</CardTitle>
            <CardDescription>
              {selectedDeals.length} of {approvedDeals?.length || 0} deals selected
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={selectAll}>
                {selectedDeals.length === approvedDeals?.length ? "Deselect All" : "Select All"}
              </Button>
              <Button
                onClick={handleExport}
                disabled={selectedDeals.length === 0 || isExporting}
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? "Generating..." : "Generate NIL Go Export"}
              </Button>
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading deals...</div>
            ) : approvedDeals && approvedDeals.length > 0 ? (
              <div className="space-y-2">
                {approvedDeals.map((deal: any) => (
                  <div
                    key={deal.id}
                    className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer"
                    onClick={() => toggleDeal(deal.id)}
                  >
                    <Checkbox
                      checked={selectedDeals.includes(deal.id)}
                      onCheckedChange={() => toggleDeal(deal.id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{deal.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {deal.athletes?.name} • {deal.brand_name} • ${Number(deal.amount).toLocaleString()}
                      </div>
                    </div>
                    {(!deal.description || !deal.approved_at) && (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No approved deals available for export
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Export History</CardTitle>
            <CardDescription>Recent NIL Go submissions</CardDescription>
          </CardHeader>
          <CardContent>
            {submissionHistory && submissionHistory.length > 0 ? (
              <div className="space-y-2">
                {submissionHistory.map((submission: any) => (
                  <div key={submission.id} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{submission.export_file_path || "NIL Go Export"}</div>
                        <div className="text-sm text-muted-foreground">
                          Submitted: {new Date(submission.submitted_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Batch ID: {submission.batch_id?.substring(0, 8)}...
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No export history yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}